import React from "react"
import { Footer } from "./Footer"
import { FormContainer, Worksheet, CheckboxYesNo, LabeledInput } from "uikit"
import { Dropdown, Radio, Grid } from "semantic-ui-react"
import { Link } from "react-router-dom"
import { useStore } from "store"

const getOwner = (incomes, income_id) => {
  let income = Object.values(incomes).find(income => income.id === income_id)
  if (income) {
    return income.person_id
  }
  return null
}

const RetirementPlanForm = ({
  data: {
    fields: {
      income_id,
      account_id,
      has_roth,
      roth_account_id,
      matchingProgram,
      match_label,
      match_rate_1,
      match_limit_1,
      match_rate_2,
      match_limit_2,
      has_nonMatching,
      employer_rate
    },
    isModified,
    onSubmit,
    isPending,
    hasError,
    error
  },
  submitLabel = "Done",
  submitHeader = "",
  isNew,
  onDelete,
  incomes,
  accounts,
  accountOptionFilters,
  incomeSourceOptions,
  matchingPrograms
}) => {
  const matchingProgramRadioGroup = matchingPrograms.map(
    ({
      label,
      match_rate_1: rate_1,
      match_limit_1: limit_1,
      match_limit_2: limit_2,
      match_rate_2: rate_2
    }) => {
      return (
        <Radio
          label={label}
          name="matchingProgram"
          key={label}
          checked={label === match_label.value}
          onChange={() => {
            match_label.onChange({}, { value: label })
            match_rate_1.onChange({}, { value: rate_1 })
            match_limit_1.onChange({}, { value: limit_1 })
            match_rate_2.onChange({}, { value: rate_2 })
            match_limit_2.onChange({}, { value: limit_2 })
          }}
        />
      )
    }
  )
  const owner_id = getOwner(incomes, income_id.value)

  const connectedAccountsOptions = accounts
    .filter(accountOptionFilters.connectedAccount(accounts, owner_id))
    .map(account => ({
      text: account.name,
      value: account.id
    }))

  const rothAccountsOptions = accounts
    .filter(accountOptionFilters.rothAccount(accounts, owner_id))
    .map(account => ({
      text: account.name,
      value: account.id
    }))

  const connectedAccountType = accountOptionFilters.connectedAccountData.type
  const connectedAccountName = accountOptionFilters.connectedAccountData.label

  const rothAccountType = accountOptionFilters.rothAccountData.type
  const rothAccountName = accountOptionFilters.rothAccountData.label

  return (
    <Worksheet error={error}>
      <Worksheet.Body>
        <Worksheet.Row
          label="Which income source is associated with this plan?"
          paddedField
        >
          <Worksheet.Field error={income_id.error}>
            <Dropdown
              fluid
              placeholder="Income Source"
              selection
              options={incomeSourceOptions}
              {...income_id}
              data-testid="income_id"
              onChange={(event, data) => {
                income_id.onChange(event, data)
                account_id.onChange(event, { value: "" })
              }}
            />
          </Worksheet.Field>
        </Worksheet.Row>
        <Worksheet.Row
          label="Which account is connected with the plan?"
          paddedField
        >
          <Worksheet.Field error={account_id.error}>
            {connectedAccountsOptions && connectedAccountsOptions.length > 0 ? (
              <Dropdown
                fluid
                placeholder="Account"
                data-testid="account_id"
                selection
                options={connectedAccountsOptions}
                {...account_id}
              />
            ) : (
              <>
                <Link
                  to={`/organize/accounts+property?add-linked-accounts`}
                  className="navlink"
                >
                  Link a {connectedAccountName}
                </Link>{" "}
                account
                <br />
                or{" "}
                <Link
                  to={{
                    pathname: `/organize/accounts+property/accounts/new/${connectedAccountType}`,
                    state: {
                      success: "/organize/income+benefits/benefits/new/"
                    }
                  }}
                  className="navlink"
                >
                  add manually
                </Link>
              </>
            )}
          </Worksheet.Field>
        </Worksheet.Row>
        <Worksheet.Row
          label="Does this plan have a Roth contribution option?"
          paddedField
        >
          <Worksheet.Field>
            <CheckboxYesNo fluid {...has_roth} name="rothContribution" />
          </Worksheet.Field>
          {has_roth.value === "true" ? (
            <Worksheet.Row
              paddedField
              label="Which account do Roth contributions go to?"
            >
              <Worksheet.Field padded error={roth_account_id.error}>
                {rothAccountsOptions && rothAccountsOptions.length > 0 ? (
                  <Dropdown
                    fluid
                    placeholder="Roth Account"
                    data-testid="roth_account_id"
                    selection
                    options={rothAccountsOptions}
                    {...roth_account_id}
                  />
                ) : (
                  <>
                    <Link
                      to={`/organize/accounts+property?add-linked-accounts`}
                      className="navlink"
                    >
                      Link a {rothAccountName}
                    </Link>{" "}
                    account
                    <br />
                    or{" "}
                    <Link
                      to={`/organize/accounts+property/accounts/new/${rothAccountType}`}
                      className="navlink"
                    >
                      add manually
                    </Link>
                  </>
                )}
              </Worksheet.Field>
            </Worksheet.Row>
          ) : null}
        </Worksheet.Row>
        <Worksheet.Row
          label="What matching program does your employer offer?"
          paddedField
        >
          <Worksheet.Field>
            <div
              className="WorksheetField__RadioGroup"
              data-testid="employerMatch"
            >
              {matchingProgramRadioGroup}
            </div>
          </Worksheet.Field>
        </Worksheet.Row>

        {match_label.value === "Other" ? (
          <Worksheet.RowCollection>
            <Worksheet.Row
              label="What is the first matching tier"
              description={
                'Your employer may offer to "Match" a portion of your contribution (often 50% or 100%) "Up To" a percentage of your salary (often 3% or 6%).'
              }
              paddedField
            >
              <Worksheet.Field padded error={match_limit_1.error}>
                <Grid columns="equal">
                  <Grid.Row style={{ padding: "10px 0px 0px 0px" }}>
                    <Grid.Column width={8}>
                      <span>Match</span>
                    </Grid.Column>
                    <Grid.Column width={8}>
                      <span>Up To</span>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row style={{ padding: "0px" }}>
                    <Grid.Column width={8}>
                      <LabeledInput
                        {...match_rate_1}
                        label=""
                        iconText="%"
                        placeholder="0"
                      />
                    </Grid.Column>
                    <Grid.Column width={8}>
                      <LabeledInput
                        {...match_limit_1}
                        label=""
                        iconText="%"
                        placeholder="0"
                      />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Worksheet.Field>
            </Worksheet.Row>
            <Worksheet.Row
              label="What is the second matching tier?"
              description={
                <>
                  Leave blank if plan has only one tier. The second tier limit
                  is incremental. If your employer first matches 100% up to 3%
                  compensation and then 50% of contributions between 3% and 5%
                  of your compensation, then you should enter "100%" and "3%"
                  for the first tier and "50%" and "2%" for the second tier.
                </>
              }
              paddedField
            >
              <Worksheet.Field padded error={match_limit_2.error}>
                <Grid columns="equal">
                  <Grid.Row style={{ padding: "10px 0px 0px 0px" }}>
                    <Grid.Column width={8}>
                      <span>Match</span>
                    </Grid.Column>
                    <Grid.Column width={8}>
                      <span>On Next</span>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row style={{ padding: "0px" }}>
                    <Grid.Column width={8}>
                      <LabeledInput
                        {...match_rate_2}
                        label=""
                        iconText="%"
                        placeholder="0"
                      />
                    </Grid.Column>
                    <Grid.Column width={8}>
                      <LabeledInput
                        {...match_limit_2}
                        label=""
                        iconText="%"
                        placeholder="0"
                      />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Worksheet.Field>
            </Worksheet.Row>
          </Worksheet.RowCollection>
        ) : null}

        <Worksheet.Row label="Does your employer make non-matching contributions?">
          <Worksheet.Field>
            <CheckboxYesNo
              fluid
              {...has_nonMatching}
              name="nonMatchingContribution"
            />
          </Worksheet.Field>
          {has_nonMatching.value === "true" ? (
            <Worksheet.Row
              paddedField
              label="What percentage of your income does your employer contribute?"
            >
              <Worksheet.Field padded error={employer_rate.error}>
                <LabeledInput
                  {...employer_rate}
                  label=""
                  iconText="%"
                  placeholder="0"
                />
              </Worksheet.Field>
            </Worksheet.Row>
          ) : null}
        </Worksheet.Row>
      </Worksheet.Body>
      <Footer
        isNew={isNew}
        submitLabel={submitLabel}
        onSubmit={onSubmit}
        onDelete={onDelete}
        isModified={isModified}
        deleteLabel="Delete Retirement Plan"
        error={error}
      />
    </Worksheet>
  )
}

const RetirementPlanFormContainer = FormContainer(
  {
    income_id: {
      type: "string"
    },
    account_id: {
      type: "string",
      required: true
    },

    has_roth: {
      type: "boolean",
      defaultValue: "false"
    },

    roth_account_id: {
      type: "string",
      required: false
    },

    match_label: {
      type: "string",
      defaultValue: "No matching program"
    },
    match_rate_1: {
      type: "decimal",
      required: false
    },
    match_limit_1: {
      type: "decimal",
      required: false
    },
    match_rate_2: {
      type: "decimal",
      required: false
    },
    match_limit_2: {
      type: "decimal",
      required: false
    },
    has_nonMatching: {
      type: "boolean",
      required: false,
      defaultValue: "false"
    },
    employer_rate: {
      type: "decimal",
      required: false
    }
  },
  (
    {
      fields: {
        match_label,
        match_limit_1,
        match_rate_2,
        match_limit_2,
        has_roth,
        roth_account_id,
        has_nonMatching,
        employer_rate
      }
    },
    ...form
  ) => {
    if (match_label.value === "Other") {
      if (
        match_limit_1.value !== "" &&
        (match_limit_1.value < 1 || match_limit_1.value > 100)
      )
        return {
          fields: {
            match_limit_1:
              "The match limit should be an integer between 1 and 100."
          }
        }
      if (
        match_limit_2.value !== "" &&
        match_rate_2.value !== "" &&
        match_rate_2.value !== "0" &&
        (match_limit_2.value < 0 ||
          match_limit_2.value > 100 - match_limit_1.value)
      )
        return {
          fields: {
            match_limit_2: `The match limit should be an integer between 1 and ${100 -
              match_limit_1.value}.`
          }
        }
    }
    if (has_roth.value === "true" && !roth_account_id.value)
      return {
        fields: {
          roth_account_id: "This field is required."
        }
      }
    if (has_nonMatching.value === "true" && employer_rate.value === "") {
      return {
        fields: {
          employer_rate: "This field is required."
        }
      }
    }
  }
)(RetirementPlanForm)

const RetirementPlan = ({ household, benefit, type, ...formProps }) => {
  const [incomes] = useStore("householdIncomes", household.id)
  const [people] = useStore("householdPeople", household.id)
  const [accounts] = useStore("householdAccounts", household.id)
  const [employers] = useStore("householdEmployers", household.id)
  const [benefits, { addBenefit, updateBenefit, deleteBenefit }] = useStore(
    "householdBenefits",
    household.id
  )

  const isNew = benefit === null
  let incomesWithBenefit = Object.values(benefits)
    .filter(({ type: benefitType }) => benefitType === type)
    .map(({ income_id }) => income_id)
  const matchingPrograms = [
    {
      label: "No matching",
      match_rate_1: "0",
      match_limit_1: "0",
      match_rate_2: null,
      match_limit_2: null
    },
    {
      label: "100% match up to 3% salary",
      match_rate_1: "100",
      match_limit_1: "3",
      match_rate_2: null,
      match_limit_2: null
    },
    {
      label: "50% match up to 6% salary",
      match_rate_1: "50",
      match_limit_1: "6",
      match_rate_2: null,
      match_limit_2: null
    },
    {
      label: "Other",
      match_rate_1: (benefit && benefit.match_rate_1) || null,
      match_limit_1: (benefit && benefit.match_limit_1) || null,
      match_rate_2: (benefit && benefit.match_rate_2) || null,
      match_limit_2: (benefit && benefit.match_limit_2) || null
    }
  ]
  let values = {
    income_id:
      Object.values(incomes).length === 1 ? Object.values(incomes)[0].id : null
  }
  if (!isNew) {
    values = Object.assign({}, benefit) //TODO
    const {
      match_rate_1,
      match_limit_1,
      match_rate_2,
      match_limit_2,
      employer_rate
    } = values
    let match_label = ""
    const defaultMatchingProgram = matchingPrograms.find(
      ({ match_rate_1: rate, match_limit_1: limit }) =>
        rate === match_rate_1 + "" &&
        limit === match_limit_1 + "" &&
        !match_rate_2 &&
        !match_limit_2
    )
    if (defaultMatchingProgram) {
      match_label = defaultMatchingProgram.label
    } else {
      match_label = "Other"
    }
    let has_nonMatching = employer_rate && employer_rate > 0 ? true : false
    Object.assign(values, {
      match_label,
      has_nonMatching
    })
    incomesWithBenefit = incomesWithBenefit.filter(
      id => id !== values.income_id
    )
  }

  const hasSpouse = Object.values(people).some(({ role }) => role === "spouse")
  const incomeSourceOptions = Object.values(incomes)
    .filter(({ id }) => !incomesWithBenefit.includes(id))
    .map(income => {
      const employer = Object.values(employers).find(
        ({ id }) => id === income.employer_id
      )
      const incomeName = employer ? employer.name : income.description
      const text =
        hasSpouse && income.person_id
          ? `${incomeName} (${people[income.person_id].first_name})`
          : incomeName
      return {
        text,
        value: income.id
      }
    })

  if (values.match_limit_2 && values.match_limit_2 !== "") {
    // API returns cumulative match limit, but users find
    // incremental more intuitive
    values.match_limit_2 -= values.match_limit_1
  }

  const submit = isNew
    ? async values => {
        delete values.match_label
        if (!values.has_nonMatching) {
          Object.assign(values, {
            employer_rate: null
          })
        }
        delete values.has_nonMatching
        if (values.match_limit_2 && values.match_limit_2 !== "") {
          // API expects cumulative match limit, but users find
          // incremental more intuitive
          values.match_limit_2 += values.match_limit_1
        }
        await addBenefit(
          Object.assign(values, {
            type,
            household_id: household.id
          })
        )
      }
    : async values => {
        if (values.match_label !== "Other") {
          Object.assign(values, {
            match_limit_2: null,
            match_rate_2: null
          })
        }
        delete values.match_label
        if (!values.has_nonMatching) {
          Object.assign(values, { employer_rate: null })
        }
        delete values.has_nonMatching
        if (values.match_limit_2 && values.match_limit_2 !== "") {
          // API expects cumulative match limit, but users find
          // incremental more intuitive
          values.match_limit_2 += values.match_limit_1
        }
        await updateBenefit({
          id: benefit.id,
          type,
          ...values
        })
      }

  const onDelete = () =>
    deleteBenefit({
      id: benefit.id,
      household_id: household.id
    })

  return (
    <RetirementPlanFormContainer
      {...{
        isNew,
        submit,
        onDelete,
        incomes: Object.values(incomes),
        accounts: Object.values(accounts),
        incomeSourceOptions,
        matchingPrograms,
        values,
        ...formProps
      }}
    />
  )
}

export default RetirementPlan
